import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Droplet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { MaskLine } from "../components/Reveal";
import { ShareCatch } from "../components/ShareCatch";

const TIER_COPY = {
  drift_club_basic_monthly: {
    tagline: "Drift Sample / Pour Club",
    perks: [
      "Sample-size monthly drop",
      "Exclusive Color of the Month",
      "Experimental pours",
      "Potential prototype / test baits",
      "Members-only access",
      "Never know exactly what's coming",
    ],
  },
  drift_club_pro_monthly: {
    tagline: "The 5-Pack Club",
    perks: [
      "5 full retail packs",
      "Everything in the Sample Club included",
      "Color of the Month samples",
      "Experimental pours",
      "Potential prototype / test baits",
      "First access to limited releases",
      "Free shipping",
    ],
  },
};

export default function Club() {
  const [tiers, setTiers] = useState([]);
  const [color, setColor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState("");
  const [email, setEmail] = useState("");
  const [managing, setManaging] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/club/tiers")
      .then((r) => {
        setTiers(r.data.tiers || []);
        setColor(r.data.color_of_month || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const subscribe = async (lookup_key) => {
    setSubscribing(lookup_key);
    try {
      const { data } = await api.post("/club/subscribe", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setSubscribing("");
    }
  };

  const openPortal = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setManaging(true);
    try {
      const { data } = await api.post("/club/portal", { email: email.trim() });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No subscription found");
      setManaging(false);
    }
  };

  const hasColor = color && (color.name || color.image);

  return (
    <div className="bg-[#04070A] min-h-screen" data-testid="club-page">
      <Nav />

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-36 pb-16">
        <MaskLine delay={0.1}>
          <span className="font-mono-plex text-xs tracking-[0.3em] uppercase text-orange-500">Drift Tackle Club</span>
        </MaskLine>
        <h1 className="font-display text-6xl sm:text-8xl uppercase tracking-tighter mt-5 leading-[0.85]">
          <MaskLine delay={0.25}>New Color,</MaskLine>
          <MaskLine delay={0.38}>Every Month</MaskLine>
        </h1>
        <p className="text-slate-400 text-lg leading-relaxed mt-6 max-w-xl">
          Join the club and get fresh-poured colors and samples shipped to your door before they ever hit the shop.
        </p>
      </section>

      {hasColor && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20" data-testid="color-of-month">
          <div className="grid lg:grid-cols-2 gap-10 items-center border border-white/5 bg-[#0A1118] overflow-hidden">
            {color.image ? (
              <img src={color.image} alt={color.name} className="w-full h-full max-h-[480px] object-cover" />
            ) : (
              <div className="w-full h-64 lg:h-full bg-gradient-to-br from-orange-500/20 to-transparent flex items-center justify-center">
                <Droplet className="w-16 h-16 text-orange-500" />
              </div>
            )}
            <div className="p-8 lg:p-12">
              <span className="font-mono-plex text-xs tracking-[0.3em] uppercase text-orange-500">This Month&rsquo;s Color</span>
              <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-4">{color.name}</h2>
              {color.description && (
                <p className="text-slate-400 text-base leading-relaxed mt-5 whitespace-pre-line">{color.description}</p>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
        {loading ? (
          <p className="text-slate-500 font-mono-plex text-sm tracking-widest uppercase">Loading plans…</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {tiers.map((t, i) => {
              const copy = TIER_COPY[t.lookup_key] || { tagline: t.label, perks: [] };
              const pro = t.lookup_key === "drift_club_pro_monthly";
              return (
                <motion.div
                  key={t.lookup_key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.19, 1, 0.22, 1] }}
                  className={`relative flex flex-col p-8 border ${
                    pro ? "border-orange-500/50 bg-orange-500/[0.04]" : "border-white/10 bg-[#0A1118]"
                  }`}
                  data-testid={`tier-${t.lookup_key}`}
                >
                  {pro && (
                    <span className="absolute -top-3 left-8 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-[0.2em] uppercase px-3 py-1">
                      Best Value
                    </span>
                  )}
                  <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-orange-500">{copy.tagline}</span>
                  <div className="flex items-end gap-2 mt-4">
                    <span className="font-display text-5xl tracking-tight">${t.amount.toFixed(2)}</span>
                    <span className="font-mono-plex text-xs tracking-widest uppercase text-slate-500 mb-2">/ month · shipped</span>
                  </div>
                  <ul className="mt-8 space-y-3 flex-1">
                    {copy.perks.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed">
                        <Check className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => subscribe(t.lookup_key)}
                    disabled={!!subscribing}
                    className={`mt-8 flex items-center justify-center gap-2 font-mono-plex text-xs tracking-[0.2em] uppercase py-4 transition-colors disabled:opacity-60 ${
                      pro
                        ? "bg-orange-500 text-[#04070A] hover:bg-orange-600"
                        : "border border-white/20 text-slate-200 hover:bg-white/5"
                    }`}
                    data-testid={`subscribe-${t.lookup_key}`}
                  >
                    {subscribing === t.lookup_key ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                      </>
                    ) : (
                      "Join the Club"
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-20 grid md:grid-cols-2 gap-10 border-t border-white/5 pt-12">
          <div data-testid="club-share-catch">
            <h3 className="font-display text-2xl uppercase tracking-tight">Show off your catch</h3>
            <p className="text-slate-400 text-sm mt-2 mb-5">
              Landed a good one on a Drift bait? Share the photo — approved shots go up in our public gallery.
            </p>
            <ShareCatch label="Share Your Catch" variant="outline" testid="club-share" />
          </div>

          <div data-testid="manage-subscription">
          <h3 className="font-display text-2xl uppercase tracking-tight">Already a member?</h3>
          <p className="text-slate-400 text-sm mt-2">
            Manage or cancel your subscription anytime. Enter the email you signed up with.
          </p>
          <form onSubmit={openPortal} className="flex flex-col sm:flex-row gap-3 mt-5">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-sm"
              data-testid="portal-email"
            />
            <button
              type="submit"
              disabled={managing}
              className="flex items-center justify-center gap-2 border border-white/20 font-mono-plex text-xs tracking-[0.2em] uppercase px-6 py-3 hover:bg-white/5 transition-colors disabled:opacity-60"
              data-testid="portal-submit"
            >
              {managing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Manage"}
            </button>
          </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
